import { useState, useEffect, useRef, useCallback, type FC } from 'react';

export type CaptureType = 'front' | 'back' | 'selfie';

interface IdPhotoCaptureProps {
  type: CaptureType;
  /** Called with the real image. `null` clears a previous shot ("Chụp lại"). */
  onCapture: (photo: { blob: Blob; previewUrl: string } | null) => void;
  photo: { blob: Blob; previewUrl: string } | null;
}

const JPEG_QUALITY = 0.85;
/** Long edge cap. A phone camera frame is far larger than an OCR needs, and the
 * upload endpoint accepts 25mb — no reason to spend a farmer's mobile data on it. */
const MAX_EDGE = 1600;

function drawScaled(source: HTMLVideoElement | HTMLImageElement, sw: number, sh: number): HTMLCanvasElement {
  const scale = Math.min(1, MAX_EDGE / Math.max(sw, sh));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(sw * scale);
  canvas.height = Math.round(sh * scale);
  canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Không tạo được ảnh JPEG'))), 'image/jpeg', JPEG_QUALITY);
  });
}

/** Cheap, on-device blur/exposure check (Feedback 05, mục 9) — a soft warning only,
 * never a hard block: the user explicitly asked NOT to add OCR/data validation,
 * just to cut down on unusable photos (mờ/cháy sáng) reaching review. Runs on a
 * small downsized copy so it stays instant even on a low-end phone. */
const QUALITY_SAMPLE_EDGE = 200;
const BLUR_VARIANCE_THRESHOLD = 90; // below this, the image is likely out of focus
const DARK_LUMA_THRESHOLD = 45; // 0-255 mean luma
const BRIGHT_LUMA_THRESHOLD = 225;

interface QualityCheck {
  blurry: boolean;
  tooDark: boolean;
  tooBright: boolean;
}

function checkImageQuality(source: HTMLCanvasElement): QualityCheck {
  const scale = QUALITY_SAMPLE_EDGE / Math.max(source.width, source.height);
  const w = Math.max(1, Math.round(source.width * scale));
  const h = Math.max(1, Math.round(source.height * scale));
  const sample = document.createElement('canvas');
  sample.width = w;
  sample.height = h;
  const ctx = sample.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { blurry: false, tooDark: false, tooBright: false };
  ctx.drawImage(source, 0, 0, w, h);

  const { data } = ctx.getImageData(0, 0, w, h);
  const luma = new Float32Array(w * h);
  let lumaSum = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    luma[p] = l;
    lumaSum += l;
  }
  const meanLuma = lumaSum / luma.length;

  // Sharpness proxy: variance of the horizontal Laplacian (|2*center - left - right|).
  // A blurry photo has little pixel-to-pixel edge contrast, so this variance is low.
  let edgeSum = 0;
  let edgeCount = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 1; x < w - 1; x++) {
      const idx = y * w + x;
      const edge = Math.abs(2 * luma[idx] - luma[idx - 1] - luma[idx + 1]);
      edgeSum += edge * edge;
      edgeCount++;
    }
  }
  const edgeVariance = edgeCount > 0 ? edgeSum / edgeCount : 0;

  return {
    blurry: edgeVariance < BLUR_VARIANCE_THRESHOLD,
    tooDark: meanLuma < DARK_LUMA_THRESHOLD,
    tooBright: meanLuma > BRIGHT_LUMA_THRESHOLD,
  };
}

function qualityMessage(q: QualityCheck): string | null {
  if (q.blurry) return 'Ảnh có vẻ bị mờ — hãy giữ yên máy, đợi camera lấy nét rồi chụp lại để rõ hơn.';
  if (q.tooDark) return 'Ảnh hơi tối — hãy chụp ở nơi đủ sáng rồi thử lại.';
  if (q.tooBright) return 'Ảnh bị chói sáng — tránh ánh sáng chiếu thẳng rồi thử lại.';
  return null;
}

/**
 * Captures a real CCCD/selfie photo from the live camera only.
 *
 * Replaces MockCameraCapture, which opened the camera but whose `onCapture()`
 * took no argument: it never read a frame, so no image data existed anywhere and
 * EkycFlow had to invent placeholder file ids. It also offered a "Dùng ảnh mẫu"
 * shortcut that submitted nothing at all, which is why eKYC still felt like a
 * demo. Both are gone: a submission now carries the caller's actual photos.
 *
 * Deliberately no gallery/file-picker fallback: eKYC exists to prove the photo
 * was taken now, of the person actually completing the flow, and a picker that
 * accepts any existing image undermines exactly that. A camera that fails to
 * open (denied permission, no hardware) blocks the flow rather than falling
 * back to a picker — accepted trade-off, not an oversight.
 */
const IdPhotoCapture: FC<IdPhotoCaptureProps> = ({ type, onCapture, photo }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Holds a just-captured, still-unconfirmed shot while a soft quality warning is
  // shown — the user picks "Chụp lại" or "Vẫn dùng ảnh này" rather than the photo
  // silently going straight through.
  const [pendingShot, setPendingShot] = useState<{ blob: Blob; previewUrl: string; warning: string } | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Trình duyệt này không hỗ trợ camera. Hãy dùng một trình duyệt hoặc thiết bị khác để xác thực.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: type === 'selfie' ? 'user' : 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      // Best-effort continuous autofocus — a non-standard constraint some Android
      // Chrome builds honor and every other browser silently ignores. Never
      // required: this is purely an attempt to reduce blurry shots at the source,
      // on top of the after-capture sharpness check below.
      const [track] = stream.getVideoTracks();
      try {
        // `focusMode` isn't in the standard MediaTrackConstraintSet lib types yet
        // (only some Chromium builds implement it), so the literal doesn't overlap
        // MediaTrackConstraints at all — go through `unknown` to get past the check.
        await track?.applyConstraints({ advanced: [{ focusMode: 'continuous' }] } as unknown as MediaTrackConstraints);
      } catch {
        // Unsupported on this device/browser — fine, the capability check below
        // is what actually catches a blurry result regardless.
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (err) {
      // Name the reason: "denied" and "no camera on this device" need different
      // actions from the farmer, and a single generic message helps with neither.
      // No gallery fallback is offered here on purpose — eKYC requires a live
      // capture so a photo cannot be substituted with an existing image.
      const name = err instanceof DOMException ? err.name : '';
      setCameraError(
        name === 'NotAllowedError'
          ? 'Bạn đã từ chối quyền camera. Vui lòng cho phép truy cập camera trong cài đặt trình duyệt rồi thử lại.'
          : name === 'NotFoundError'
            ? 'Không tìm thấy camera trên thiết bị này. Vui lòng dùng thiết bị có camera để xác thực.'
            : 'Không mở được camera. Vui lòng kiểm tra lại và thử lại.',
      );
    }
  }, [type]);

  useEffect(() => {
    if (!photo) startCamera();
    return stopCamera;
  }, [photo, startCamera, stopCamera]);

  // A preview URL is an object URL; leaking them keeps whole images alive in memory.
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.previewUrl); }, [photo]);

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setBusy(true);
    try {
      const canvas = drawScaled(video, video.videoWidth, video.videoHeight);
      const blob = await toJpeg(canvas);
      const warning = qualityMessage(checkImageQuality(canvas));
      if (warning) {
        // Don't stop the camera yet — "Chụp lại" should reopen straight into a
        // live preview, not a re-request for permission.
        setPendingShot({ blob, previewUrl: URL.createObjectURL(blob), warning });
        return;
      }
      stopCamera();
      onCapture({ blob, previewUrl: URL.createObjectURL(blob) });
    } catch {
      setCameraError('Không chụp được ảnh. Vui lòng thử lại.');
    } finally {
      setBusy(false);
    }
  };

  const retakePendingShot = () => {
    if (pendingShot) URL.revokeObjectURL(pendingShot.previewUrl);
    setPendingShot(null);
  };

  const usePendingShotAnyway = () => {
    if (!pendingShot) return;
    stopCamera();
    onCapture({ blob: pendingShot.blob, previewUrl: pendingShot.previewUrl });
    setPendingShot(null);
  };

  const title = type === 'front' ? 'Mặt trước CCCD' : type === 'back' ? 'Mặt sau CCCD' : 'Ảnh chân dung';
  const hint =
    type === 'front'
      ? 'Đặt CCCD trên mặt phẳng, chụp rõ toàn bộ mặt trước, không bị chói sáng'
      : type === 'back'
        ? 'Chụp rõ mặt sau CCCD, đảm bảo thông tin dễ đọc, không bị mờ'
        : 'Chụp ảnh chân dung rõ mặt, không đeo kính đen, nhìn thẳng vào camera';
  const frameClass = type === 'selfie' ? 'aspect-square max-w-[240px] mx-auto rounded-full' : 'aspect-[3/2] rounded-xl';

  if (photo) {
    return (
      <div className="w-full">
        <div className={`w-full overflow-hidden relative ${frameClass} bg-gray-900`}>
          <img src={photo.previewUrl} alt={`Ảnh đã chụp: ${title}`} data-testid={`ekyc-preview-${type}`} className="w-full h-full object-cover" />
        </div>
        <p className="mt-3 text-center text-xs text-agrida-700 font-medium">
          Đã chụp {title.toLowerCase()} · {(photo.blob.size / 1024).toFixed(0)} KB
        </p>
        <button
          type="button"
          onClick={() => onCapture(null)}
          data-testid={`ekyc-retake-${type}`}
          className="mt-3 w-full border border-slate-200 text-slate-700 font-semibold py-3 rounded-xl text-sm min-h-[48px]"
        >
          Chụp lại
        </button>
      </div>
    );
  }

  if (pendingShot) {
    return (
      <div className="w-full">
        <div className={`w-full overflow-hidden relative ${frameClass} bg-gray-900`}>
          <img src={pendingShot.previewUrl} alt={`Ảnh vừa chụp: ${title}`} className="w-full h-full object-cover" />
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5">
          <span className="w-4 h-4 mt-0.5 flex items-center justify-center shrink-0"><i className="ri-error-warning-line text-amber-600" /></span>
          <p className="text-xs text-amber-800">{pendingShot.warning}</p>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={retakePendingShot}
            className="flex-1 bg-agrida-600 hover:bg-agrida-700 text-white font-semibold py-3 rounded-xl text-sm min-h-[48px]"
          >
            Chụp lại
          </button>
          <button
            type="button"
            onClick={usePendingShotAnyway}
            className="flex-1 border border-slate-200 text-slate-700 font-semibold py-3 rounded-xl text-sm min-h-[48px]"
          >
            Vẫn dùng ảnh này
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className={`w-full overflow-hidden relative ${frameClass} bg-gray-900`}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${type === 'selfie' ? 'scale-x-[-1]' : ''} ${cameraReady ? '' : 'invisible'}`}
        />
        {!cameraReady && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
            {cameraError ? (
              <>
                <span className="w-10 h-10 flex items-center justify-center"><i className="ri-camera-off-line text-gray-400 text-2xl" /></span>
                <p className="text-xs text-gray-300 text-center" role="alert">{cameraError}</p>
              </>
            ) : (
              <>
                <span className="w-10 h-10 flex items-center justify-center"><i className="ri-camera-line text-gray-400 text-2xl animate-pulse" /></span>
                <span className="text-xs text-gray-400">Đang mở camera...</span>
              </>
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 text-center mt-3 mb-4">{hint}</p>

      <button
        type="button"
        onClick={captureFromCamera}
        disabled={!cameraReady || busy}
        data-testid={`ekyc-shoot-${type}`}
        className="w-full bg-agrida-600 hover:bg-agrida-700 disabled:bg-gray-300 text-white font-semibold py-3.5 rounded-xl transition-colors disabled:cursor-not-allowed min-h-[48px] text-sm flex items-center justify-center gap-2"
      >
        <span className="w-5 h-5 flex items-center justify-center"><i className="ri-camera-line" /></span>
        {busy ? 'Đang xử lý...' : `Chụp ${title.toLowerCase()}`}
      </button>
    </div>
  );
};

export default IdPhotoCapture;
